import { Injectable } from '@angular/core';
import { parseFixedOffsetTimeZone, parseShortOffsetMinutes } from '../helpers/timezone-offset-parser.helper';

@Injectable({
  providedIn: 'root',
})
export class TimeZoneConstants {
  // Backend tenant migrations (SystemTimeZoneOffsetMap) store legacy fixed-offset tenants using
  // these POSIX Etc/GMT ids, but Intl.supportedValuesOf('timeZone') never returns them — union
  // them in explicitly so the dropdown has a matching option for every migrated tenant.
  private static readonly additionalTimeZoneIds: readonly string[] = [
    'Etc/GMT+12',
    'Etc/GMT+11',
    'Etc/GMT+10',
    'Etc/GMT+9',
    'Etc/GMT+8',
    'Etc/GMT+7',
    'Etc/GMT+6',
    'Etc/GMT+5',
    'Etc/GMT+4',
    'Etc/GMT+3',
    'Etc/GMT+2',
    'Etc/GMT+1',
    'Etc/UTC',
    'Etc/GMT-1',
    'Etc/GMT-2',
    'Etc/GMT-3',
    'Etc/GMT-4',
    'Etc/GMT-5',
    'Etc/GMT-6',
    'Etc/GMT-7',
    'Etc/GMT-8',
    'Etc/GMT-9',
    'Etc/GMT-10',
    'Etc/GMT-11',
    'Etc/GMT-12',
    'Etc/GMT-13',
    'Etc/GMT-14',
  ];

  // Backend SystemTimeZoneOffsetMap.OffsetToRegionalZone maps legacy half/45-minute offsets (which
  // have no Etc/GMT equivalent) to these regional zones. Listed explicitly because some Intl
  // implementations only return the old alias (e.g. "Asia/Calcutta") instead of the canonical id.
  private static readonly regionalTimeZoneIds: readonly string[] = [
    'Pacific/Marquesas',
    'America/St_Johns',
    'Asia/Tehran',
    'Asia/Kabul',
    'Asia/Kolkata',
    'Asia/Kathmandu',
    'Asia/Yangon',
    'Australia/Eucla',
    'Australia/Darwin',
    'Australia/Lord_Howe',
    'Pacific/Chatham',
  ];

  // Old aliases that Intl.supportedValuesOf('timeZone') may return for the canonical ids above;
  // mapped so the dropdown never shows the same zone twice under two names.
  private static readonly legacyTimeZoneAliases: Readonly<Record<string, string>> = {
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Rangoon': 'Asia/Yangon',
  };

  /**
   * Maps a known old time zone alias (e.g. "Asia/Calcutta") to the canonical id used by the
   * dropdown options (e.g. "Asia/Kolkata"), so a saved legacy alias still matches an option.
   *
   * @param timeZone Time zone id to normalize; may be null/undefined.
   * @returns The canonical id for a known alias, otherwise `timeZone` unchanged.
   */
  static normalizeTimeZoneId(timeZone: string): string {
    return TimeZoneConstants.legacyTimeZoneAliases[timeZone] ?? timeZone;
  }

  static get timeZones(): { [name: string]: string } {
    const entries: { id: string; offsetMinutes: number }[] = [];
    const ids = new Set<string>([
      ...Intl.supportedValuesOf('timeZone').map(id => TimeZoneConstants.normalizeTimeZoneId(id)),
      ...TimeZoneConstants.additionalTimeZoneIds,
      ...TimeZoneConstants.regionalTimeZoneIds,
    ]);

    for (const id of ids) {
      try {
        const offsetMinutes = TimeZoneConstants.getCurrentOffsetInMinutes(id);
        entries.push({ id, offsetMinutes });
      } catch {
        // Skip zone ids Intl cannot resolve in this environment.
      }
    }

    entries.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id));

    const timeZones: { [name: string]: string } = {};

    for (const entry of entries) {
      timeZones[entry.id] = `${entry.id} (UTC${TimeZoneConstants.formatOffset(entry.offsetMinutes)})`;
    }

    return timeZones;
  }

  /**
   * Adds the tenant's own legacy fixed-offset value (e.g. "+0330") as an extra option, so the
   * dropdown can still show it as selected. The backend migration keeps such a value as-is when
   * no DST-free IANA/Etc equivalent exists. Fixed offsets are deliberately NOT part of
   * {@link timeZones}, so they can never be picked for a new selection — only the stored value
   * is offered, and it is placed first so it is easy to find.
   *
   * @param timeZones The general option map, see {@link timeZones}.
   * @param currentValue The tenant's currently stored time zone value; may be null/undefined.
   * @param fixedOffsetSuffix Localized "fixed offset" text shown in parentheses after the label.
   * @returns `timeZones` unchanged when `currentValue` is not a fixed offset; otherwise a new map
   * with an extra `currentValue` -> "UTC+03:30 (fixed offset)" entry first.
   */
  static withFixedOffsetOption(
    timeZones: { [name: string]: string },
    currentValue: string,
    fixedOffsetSuffix: string
  ): { [name: string]: string } {
    const offsetMinutes = parseFixedOffsetTimeZone(currentValue);

    if (offsetMinutes === null) {
      return timeZones;
    }

    return {
      [currentValue]: `UTC${TimeZoneConstants.formatOffset(offsetMinutes)} (${fixedOffsetSuffix})`,
      ...timeZones,
    };
  }

  private static getCurrentOffsetInMinutes(timeZone: string): number {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' });
    const offsetPart = formatter.formatToParts(new Date()).find(part => part.type === 'timeZoneName');

    if (!offsetPart) {
      throw new Error(`Unable to resolve UTC offset for time zone: ${timeZone}`);
    }

    return parseShortOffsetMinutes(offsetPart.value);
  }

  private static formatOffset(offsetMinutes: number): string {
    const sign = offsetMinutes < 0 ? '-' : '+';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (absMinutes % 60).toString().padStart(2, '0');

    return `${sign}${hours}:${minutes}`;
  }
}
